// ===========================================
// SERVICE: Tareas recurrentes (CORE) — ORR-304
// ===========================================

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Prioridad } from '@/types/database'
import type { DiaModo, Frecuencia } from '@/components/tareas/forms/recurrenciaSchema'
import { CONDICIONES_IVA, type CondicionIVA } from '@/workspaces/contable/types/faceta'
import { etiquetasClienteService } from './etiquetasClienteService'
import { TIPOS_PERSONA_GRUPO, type TipoPersonaGrupo } from './tareasGrupoService'

/**
 * Fila de `tareas_recurrentes` más los nombres que resuelve `listar()`. Los tipos generados de la
 * tabla no traen `condicion_iva`, `impuesto_id` ni `tipo_persona`, que la tabla tiene, así que cada lectura castea
 * el resultado a esta interfaz.
 */
export interface TareaRecurrente {
  id: string
  organizacion_id: string
  titulo: string
  descripcion: string | null
  cliente_id: string | null
  // ORR-338 — resuelto aparte en `listar()`, viene de `vista_clientes_completos`.
  // La tabla no lo trae: el nombre vive en la vista, no en `clientes`.
  cliente_nombre: string | null
  // Grupo de clientes: a lo sumo uno de los cuatro, y excluyente con `cliente_id` por el CHECK
  // `tareas_recurrentes_destino_unico`. Con grupo, cada ocurrencia abanica en una tarea por
  // cliente del grupo.
  etiqueta_id: string | null
  condicion_iva: string | null
  impuesto_id: string | null
  tipo_persona: string | null
  // Resuelto aparte en `listar()`, igual que `cliente_nombre`. Ej.: "IVA: Monotributo".
  grupo_descripcion: string | null
  asignado_a: string | null
  prioridad: Prioridad
  workspace: string
  frecuencia: Frecuencia
  dia_modo: DiaModo
  dia_del_mes: number | null
  dia_semana: number | null
  correr_a_habil: boolean
  activa: boolean
  fecha_inicio: string
  proxima_fecha: string | null
  ultima_generada: string | null
  created_by: string
  created_at: string
  updated_at: string
}

export type TareaRecurrenteInput = Omit<
  TareaRecurrente,
  | 'id'
  | 'proxima_fecha'
  | 'ultima_generada'
  | 'created_at'
  | 'updated_at'
  | 'activa'
  | 'cliente_nombre'
  | 'grupo_descripcion'
> & { activa?: boolean }

/**
 * Intentos de `generar_tareas_de_recurrente` que cortan con 55P03. La RPC espera un lock 2 s y
 * corta si lo tiene otra transacción, como una corrida del cron o el borrado de una ocurrencia.
 */
export const INTENTOS_OCURRENCIA_OCUPADA = 3

/** Espera antes de reintentar un 55P03, multiplicada por el número de intento. */
export const ESPERA_REINTENTO_MS = 300

const esperar = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/** Fecha de hoy en Argentina, en formato ISO corto. Espeja `hoy_ar()` de la base. */
export const hoyEnArgentina = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date())

/** Fila de `app_settings` con los días de anticipación. La lee también el cron `scheduled-tareas-recurrentes`. */
export const ANTICIPACION_CLAVE = 'tareas_recurrentes_anticipacion_dias'

/** Anticipación que se usa si la fila falta o no es un entero entre 0 y `ANTICIPACION_MAX`. El cron usa el mismo valor. */
export const ANTICIPACION_DEFAULT = 5

/** Anticipación más alta que se acepta, en días. El cron usa el mismo tope. */
export const ANTICIPACION_MAX = 365

/**
 * Último día, en fecha argentina, cuyas ocurrencias ya se crean: hoy más `dias`. Es la misma
 * ventana que el cron le pasa a `generar_tareas_recurrentes` como `p_hasta`.
 */
export const limiteDeAnticipacion = (dias: number): string => {
  const fecha = new Date(`${hoyEnArgentina()}T00:00:00Z`)
  fecha.setUTCDate(fecha.getUTCDate() + dias)
  return fecha.toISOString().slice(0, 10)
}

/** Cuándo aparece una tarea en el panel respecto de su vencimiento. Ej.: "15 días antes". */
export const textoAnticipacion = (dias: number): string =>
  dias === 0 ? 'el mismo día' : dias === 1 ? '1 día antes' : `${dias} días antes`

export const tareasRecurrentesService = {
  async listar(
    supabase: SupabaseClient<Database>,
    organizacionId: string
  ): Promise<TareaRecurrente[]> {
    const { data, error } = await supabase
      .from('tareas_recurrentes')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .order('activa', { ascending: false })
      .order('proxima_fecha', { ascending: true })

    if (error) throw error
    const recurrentes = (data ?? []) as TareaRecurrente[]

    // `tareas_recurrentes` no trae el nombre del cliente: vive en la vista, no en la tabla base.
    // Segunda consulta acotada a los ids presentes.
    const clienteIds = [...new Set(recurrentes.map((r) => r.cliente_id).filter((id): id is string => !!id))]
    const nombreClientePorId = new Map<string, string | null>()

    if (clienteIds.length > 0) {
      const { data: clientesData, error: clientesError } = await supabase
        .from('vista_clientes_completos')
        .select('id, nombre_completo')
        .in('id', clienteIds)
      if (clientesError) throw clientesError
      // `id` viene nullable de la vista; sin el guard el Map tendría que aceptar claves null.
      for (const c of clientesData ?? []) {
        if (c.id) nombreClientePorId.set(c.id, c.nombre_completo)
      }
    }

    // Mismo motivo que arriba, para el grupo. Sin esto, dos repeticiones tituladas parecido
    // apuntando a grupos distintos son indistinguibles en el panel.
    const nombreEtiquetaPorId = new Map<string, string>()
    if (recurrentes.some((r) => !!r.etiqueta_id)) {
      const etiquetas = await etiquetasClienteService.listar(supabase, organizacionId)
      for (const e of etiquetas) nombreEtiquetaPorId.set(e.id, e.nombre)
    }

    const impuestoIds = [...new Set(recurrentes.map((r) => r.impuesto_id).filter((id): id is string => !!id))]
    const nombreImpuestoPorId = new Map<string, string>()
    if (impuestoIds.length > 0) {
      const { data: impuestosData, error: impuestosError } = await supabase
        .from('catalogo_impuestos')
        .select('id, nombre')
        .in('id', impuestoIds)
      if (impuestosError) throw impuestosError
      for (const i of impuestosData ?? []) nombreImpuestoPorId.set(i.id, i.nombre)
    }

    const describirGrupo = (r: TareaRecurrente): string | null => {
      if (r.etiqueta_id) {
        const nombre = nombreEtiquetaPorId.get(r.etiqueta_id)
        return nombre ? `Etiqueta: ${nombre}` : null
      }
      if (r.condicion_iva) {
        return `IVA: ${CONDICIONES_IVA[r.condicion_iva as CondicionIVA] ?? r.condicion_iva}`
      }
      if (r.impuesto_id) {
        const nombre = nombreImpuestoPorId.get(r.impuesto_id)
        return nombre ? `Impuesto: ${nombre}` : null
      }
      if (r.tipo_persona) {
        return `Tipo: ${TIPOS_PERSONA_GRUPO[r.tipo_persona as TipoPersonaGrupo] ?? r.tipo_persona}`
      }
      return null
    }

    return recurrentes.map((r) => ({
      ...r,
      cliente_nombre: r.cliente_id ? (nombreClientePorId.get(r.cliente_id) ?? null) : null,
      grupo_descripcion: describirGrupo(r),
    }))
  },

  /**
   * Inserta la repetición con el `id` que trae `input`. Un reintento con el mismo `id` choca con la
   * PK, la única restricción única de la tabla, y devuelve la fila que ya estaba guardada.
   */
  async crear(
    supabase: SupabaseClient<Database>,
    input: TareaRecurrenteInput & { id: string }
  ): Promise<TareaRecurrente> {
    const { data, error } = await supabase.from('tareas_recurrentes').insert(input).select().single()
    if (!error) return data as TareaRecurrente
    if ((error as { code?: string }).code !== '23505') throw error
    // Si la fila guardada no se puede leer, el error que se informa es el del INSERT.
    return tareasRecurrentesService.obtener(supabase, input.id).catch(() => {
      throw error
    })
  },

  async obtener(supabase: SupabaseClient<Database>, id: string): Promise<TareaRecurrente> {
    const { data, error } = await supabase.from('tareas_recurrentes').select('*').eq('id', id).single()
    if (error) throw error
    return data as TareaRecurrente
  },

  async actualizar(
    supabase: SupabaseClient<Database>,
    id: string,
    cambios: Partial<TareaRecurrenteInput>
  ): Promise<TareaRecurrente> {
    const { data, error } = await supabase
      .from('tareas_recurrentes')
      .update(cambios)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data as TareaRecurrente
  },

  async pausar(
    supabase: SupabaseClient<Database>,
    id: string,
    activa: boolean
  ): Promise<TareaRecurrente> {
    const { data, error } = await supabase
      .from('tareas_recurrentes')
      .update({ activa })
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data as TareaRecurrente
  },

  async eliminar(supabase: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await supabase.from('tareas_recurrentes').delete().eq('id', id)
    if (error) throw error
  },

  /**
   * Días de anticipación con que se crean las tareas de una repetición, leídos de `app_settings`.
   * Nunca rechaza: si la fila falta, no es un entero entre 0 y `ANTICIPACION_MAX` o la lectura falla, devuelve
   * `ANTICIPACION_DEFAULT`.
   */
  async obtenerAnticipacionDias(supabase: SupabaseClient<Database>): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', ANTICIPACION_CLAVE)
        .maybeSingle()
      if (error || !data) return ANTICIPACION_DEFAULT
      const v = data.value
      const n = typeof v === 'number' || (typeof v === 'string' && v.trim() !== '') ? Number(v) : NaN
      return Number.isInteger(n) && n >= 0 && n <= ANTICIPACION_MAX ? n : ANTICIPACION_DEFAULT
    } catch {
      return ANTICIPACION_DEFAULT
    }
  },

  /**
   * Crea en la base las tareas de la repetición que ya entran en la anticipación del cron y
   * devuelve cuántas creó. La RPC corre el mismo generador que el cron, solo para esta repetición:
   * resuelve el grupo sin las policies de quien guarda, crea todas las tareas de la ocurrencia en
   * una transacción y avisa al asignado.
   *
   * Un 55P03 se reintenta hasta `INTENTOS_OCURRENCIA_OCUPADA` veces. Cualquier otro error rechaza.
   */
  async generarPrimeraOcurrencia(supabase: SupabaseClient<Database>, recurrenteId: string): Promise<number> {
    for (let intento = 1; ; intento++) {
      const { data, error } = await supabase.rpc('generar_tareas_de_recurrente', { p_id: recurrenteId })
      if (!error) return data ?? 0
      if ((error as { code?: string }).code !== '55P03' || intento === INTENTOS_OCURRENCIA_OCUPADA) throw error
      await esperar(ESPERA_REINTENTO_MS * intento)
    }
  },
}
